import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";

const loginSchema = z.object({
  email: z.email("El email no es válido"),
  password: z.string().nonempty("La contraseña es obligatoria"),
  terms: z.boolean().refine((val) => val === true, {
    message: "Debes aceptar los términos y condiciones",
  }),
});

const LoginFormModal = ({ onClose }) => {
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
      terms: false,
    },
  });

  const handleCloseModalWrapper = (e) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  const handleLoginForm = async (data) => {
    const { email, password } = data;
    try {
      const result = await login(email, password);

      if (!result.isSuccess) {
        return setError("root.serverError", {
          type: 401,
          message: "Error al iniciar sesión intente de nuevo",
        });
      }

      if (
        ["administrador", "tecnico-1", "tecnico-2"].includes(result.user.role)
      ) {
        navigate("/admin/dashboard");
        return;
      }
      navigate("/dashboard");
    } catch (error) {
      setError("root.serverError", {
        type: "server",
        message: "Error al iniciar sesión, intente de nuevo más tarde.",
      });
    } finally {
      reset();
      onClose?.();
    }
  };

  return (
    <div
      className="bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 px-6 rounded-xl w-full max-w-[90%]"
      onClick={handleCloseModalWrapper}
    >
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 relative">
          <form onSubmit={handleSubmit(handleLoginForm)} className="space-y-6">
            {/* Email */}
            <div className="relative">
              <label
                htmlFor="modal-login-email"
                className="block text-sm font-semibold leading-5 text-gray-700"
              >
                Email address
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  id="modal-login-email"
                  {...register("email")}
                  placeholder="user@example.com"
                  autoComplete="username"
                  type="text"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5 bg-green-100"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  {errors.email && (
                    <svg
                      className="h-5 w-5 text-red-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      />
                    </svg>
                  )}
                </div>
              </div>
              {errors.email && (
                <p className="text-[10px]  text-red-600 absolute left-0 bottom-0 transform translate-y-[100%] leading-[2]">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="mt-6 relative">
              <label
                htmlFor="modal-login-password"
                className="block text-sm font-semibold leading-5 text-gray-700"
              >
                Password
              </label>
              <div className="mt-1 mb-[0.5px] rounded-md shadow-sm relative">
                <input
                  id="modal-login-password"
                  placeholder="********"
                  autoComplete="current-password"
                  disabled={isLoading}
                  type="password"
                  {...register("password")}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-500 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5 bg-green-100"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  {errors.password && (
                    <svg
                      className="h-5 w-5 text-red-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      />
                    </svg>
                  )}
                </div>
              </div>

              {errors.password && (
                <p className="text-[10px]  text-red-600 absolute left-0 bottom-0 transform translate-y-[100%] leading-[2]">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Terms Checkbox */}
            <div className="mt-6 space-y-[0.5px] relative">
              <div className="flex items-start gap-2">
                <input
                  id="modal-login-terms"
                  {...register("terms")}
                  type="checkbox"
                  disabled={isLoading}
                  className="form-checkbox h-4 w-4 transition duration-150 ease-in-out accent-brand-600"
                />
                <label
                  htmlFor="modal-login-remember-me"
                  className="block text-sm text-gray-900 mt-[-2px]"
                >
                  He leído y acepto los{" "}
                  <Link to="/terminos-condiciones" className="text-sm text-brand-300 font-semibold" >
                    Términos y Condiciones de uso
                  </Link>
                </label>
              </div>
              {errors.terms && (
                <p className="text-[10px] text-red-600 absolute left-0 bottom-0 transform translate-y-[100%]">
                  {errors.terms.message}
                </p>
              )}
            </div>

            {/* Submit */}
            <div className="mt-6">
              <span className="block w-full rounded-md shadow-sm">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent text-md font-semibold rounded-md text-white bg-brand-600 hover:opacity-90 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Iniciar sesión
                </button>
              </span>
            </div>
          </form>

          {/* Links */}
          <div className="leading-5 mt-1">
            <a
              href="#"
              className="font-semibold text-sm text-brand-300 hover:underline focus:outline-none focus:underline transition ease-in-out duration-150"
            >
              ¿Has olvidado la contraseña?
            </a>
          </div>

          <p className="text-sm leading-5">
            ¿Aún no tienes cuenta?.{" "}
            <Link
              to="/registro"
              className="text-sm font-semibold text-brand-300 hover:underline focus:outline-none focus:underline transition ease-in-out duration-150"
            >
              Crea una aquí.
            </Link>
          </p>
          {errors.root?.serverError?.type === 401 && (
            <p className="text-[10px]  text-red-600 absolute left-0 bottom-0 transform -translate-y-[2px] translate-x-[40px] leading-[2]">
              {errors.root?.serverError?.message}
            </p>
          )}
          {isLoading && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="loader text-brand-300 [--d:20px]" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginFormModal;
